import { gql } from "@apollo/client";
import makeStyles from "../../../../makeStylesV4Compat";
import { useState } from "react";
import { useAuth } from "src/components/Auth/AuthContext";
import { useURLSearchParams } from "src/models/URLSearchParams";
import { CondensedTableView_compCycle as CompCycle } from "../../../../__generated__/graphql";
import { ActionBar } from "../../../../components/Layout/ActionBar";
import CompCycleFilterBar from "../CompCycleFilterBar";
import { CompCycleSubmitChangesButtonLoadingBoundary } from "../CompCycleSubmitChangesButtonLoadingBoundary";
import { ColumnOrderProvider } from "../Contexts/ColumnOrderContext";
import { PaginationProvider } from "../Contexts/PaginationContext";
import { TableFilterProvider } from "../Contexts/TableFilterContext";
import { PhaseProgressBar } from "../PhaseProgressBar";
import { CurrencyCodeWithAll } from "./CondensedTableCurrencyPicker";
import { CondensedTableHeader2 } from "./CondensedTableHeader";
import { CondensedTablePeopleLoadingBoundary } from "./CondensedTablePeopleLoadingBoundary";

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 5rem)", // Don't scroll, let the table do it
  },
}));

type Props = {
  compCycle: CompCycle;
  isAdmin: boolean;
  userHasPhaseAssignment: boolean;
};

export function CondensedTableView({
  compCycle,
  isAdmin,
  userHasPhaseAssignment,
}: Props): JSX.Element {
  const classes = useStyles();
  const { permissions } = useAuth();
  const urlSearchParams = useURLSearchParams();
  const managerId = urlSearchParams.get("manager");
  const actingManagerEmployeeId =
    managerId != null && permissions.isHRBP()
      ? Number.parseInt(managerId)
      : null;
  const userIsViewOnly =
    !isAdmin &&
    actingManagerEmployeeId === null &&
    !userHasPhaseAssignment &&
    compCycle.phases.length > 0;

  const [nameSearch, setNameSearch] = useState<string | null>(null);

  const [selectedCurrency, setSelectedCurrency] =
    useState<CurrencyCodeWithAll>("all");

  return (
    <TableFilterProvider>
      <PaginationProvider shouldSyncURLParams>
        <ColumnOrderProvider
          cycleSettings={compCycle}
          userIsViewOnly={userIsViewOnly}
        >
          <div className={classes.root}>
            {compCycle.phases.length > 0 && (
              <PhaseProgressBar compCycle={compCycle} />
            )}
            <ActionBar
              slotLeft={<CompCycleFilterBar compCycleId={compCycle.id} />}
              slotRight={
                !userIsViewOnly && (
                  <CompCycleSubmitChangesButtonLoadingBoundary
                    compCycleId={compCycle.id}
                    nameSearch={nameSearch}
                  />
                )
              }
            />
            <CondensedTableHeader2
              onSearchTermChange={setNameSearch}
              selectedCurrency={selectedCurrency}
              onChangeSelectedCurrency={setSelectedCurrency}
              compCycle={compCycle}
              nameSearch={nameSearch}
            />
            <CondensedTablePeopleLoadingBoundary
              compCycleId={compCycle.id}
              isAdmin={isAdmin}
              nameSearch={nameSearch}
              selectedCurrency={selectedCurrency}
              compCycle={compCycle}
              isViewOnly={userIsViewOnly}
            />
          </div>
        </ColumnOrderProvider>
      </PaginationProvider>
    </TableFilterProvider>
  );
}

CondensedTableView.fragments = {
  compCycle: gql`
    ${PhaseProgressBar.fragments.compCycle}
    ${CondensedTableHeader2.fragments.compCycle2}
    fragment CondensedTableView_compCycle on CompCycle2 {
      id
      ...PhaseProgressBar_compCycle
      ...CondensedTableHeader_compCycle2
      allowSalary
      allowSalaryMarket
      allowSalaryMerit
      allowSalaryPromotion
      allowBonus
      allowEquity
      allowActualRecurringBonus
      allowTargetCommission
      allowTargetRecurringBonus
    }
  `,
};
